import { useMemo } from 'react';

import {
  Alert,
  AlertDescription,
  AlertTitle,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
  Spinner
} from 'design-system';

import { DEFAULT_CHART_HEIGHT, TABLE_ROW_HEIGHT_PX } from '@/config/constants';
import ChartShell from '@/features/charts/ChartShell';
import type { ChartSummaryProps } from '@/features/charts/ChartSummary';
import ChartSummary from '@/features/charts/ChartSummary';
import useSeriesVisibility from '@/features/charts/hooks/useSeriesVisibility';
import { applySeriesVisibility, getSeriesId } from '@/features/charts/lib/seriesVisibility';
import type {
  ChartType,
  ExtendedOptions,
  ExtendedSeriesOptionsType
} from '@/features/charts/types/highcharts';

import LegendDropdown from './LegendDropdown';
import useRegularChart from './useRegularChart';

type RegularChartProps = {
  chartSeries: ExtendedSeriesOptionsType[] | undefined;
  height?: number;
  error: boolean;
  loading: boolean;
  showLegend?: boolean;
  options: ExtendedOptions | undefined;
  chartType?: ChartType;
  chartId?: string;
  label?: string;
  rangeLabel?: string;
};

const chartKindByType = {
  forwardSimple: 'forward-curve',
  seasonal: 'seasonal',
  spot: 'stock'
} as const satisfies Record<Exclude<ChartType, undefined>, ChartSummaryProps['kind']>;

const getChartKind = (chartType: ChartType): ChartSummaryProps['kind'] =>
  chartType ? chartKindByType[chartType] : 'stock';

const RegularChart = ({
  chartSeries,
  height,
  error,
  loading,
  showLegend = true,
  options,
  chartType = 'spot',
  chartId,
  label = 'Prices',
  rangeLabel
}: RegularChartProps) => {
  const { chartContainerRef } = useRegularChart({ chartSeries });
  const chartContainerHeight = height ? height * TABLE_ROW_HEIGHT_PX : DEFAULT_CHART_HEIGHT;
  const seriesSource = options?.series ?? chartSeries;
  const { hiddenSeriesIds, setHiddenSeriesIds } = useSeriesVisibility(seriesSource);
  const chartOptions = useMemo(() => {
    if (!options) return options;
    const seriesWithVisibility = applySeriesVisibility(
      options.series ?? chartSeries,
      hiddenSeriesIds
    );
    return {
      ...options,
      series: seriesWithVisibility
    };
  }, [chartSeries, hiddenSeriesIds, options]);

  const legendSeries = useMemo(() => {
    if (!seriesSource?.length) return [];
    return seriesSource.filter(series => !series.name?.includes('Navigator'));
  }, [seriesSource]);
  const legendSeriesIds = useMemo(
    () =>
      legendSeries
        .map(series => getSeriesId(series))
        .filter((seriesId): seriesId is string => Boolean(seriesId)),
    [legendSeries]
  );
  const summary: ChartSummaryProps = {
    kind: getChartKind(chartType),
    label,
    totalSeries: legendSeriesIds.length,
    visibleSeries: legendSeriesIds.filter(seriesId => !hiddenSeriesIds.includes(seriesId)).length,
    ...(rangeLabel ? { rangeLabel } : {})
  };
  const hasChartData = summary.totalSeries > 0;

  if (error && !hasChartData) {
    return (
      <section
        aria-label={`${label} data unavailable`}
        className='flex w-full flex-col justify-center gap-3 overflow-x-hidden'
        style={{ height: chartContainerHeight }}
      >
        <Alert>
          <AlertTitle>Chart data unavailable.</AlertTitle>
          <AlertDescription>
            We couldn't retrieve your data. Please try again later.
          </AlertDescription>
        </Alert>
        <ChartSummary {...summary} />
      </section>
    );
  }

  if (loading) {
    return (
      <section
        aria-label={`${label} chart`}
        className='flex w-full items-center justify-center'
        style={{ height: chartContainerHeight }}
      >
        <Spinner
          aria-label='Loading chart'
          className='size-10'
        />
      </section>
    );
  }

  if (!hasChartData) {
    return (
      <section
        aria-label={`${label} data unavailable`}
        className='flex w-full overflow-x-hidden'
        style={{ height: chartContainerHeight }}
      >
        <Empty>
          <EmptyHeader>
            <EmptyTitle>Unable to display this data.</EmptyTitle>
            <EmptyDescription>No chart series were returned.</EmptyDescription>
            <ChartSummary {...summary} />
          </EmptyHeader>
        </Empty>
      </section>
    );
  }

  return (
    <section
      aria-label={`${label} chart`}
      className='w-full overflow-x-hidden'
    >
      <div ref={chartContainerRef}>
        {error && (
          <Alert className='mb-2'>
            <AlertTitle>Some chart data may be unavailable.</AlertTitle>
            <AlertDescription>
              We couldn't retrieve all chart data. Please try again later.
            </AlertDescription>
          </Alert>
        )}
        <div className={chartType === 'spot' ? 'regular-chart' : 'base-chart'}>
          <ChartShell
            chartId={chartId}
            containerProps={{ id: chartId }}
            isStockChart={chartType === 'spot'}
            options={chartOptions}
            summary={summary}
          />
          {showLegend && legendSeries.length > 0 && (
            <LegendDropdown
              hiddenSeriesIds={hiddenSeriesIds}
              series={legendSeries}
              setHiddenSeriesIds={setHiddenSeriesIds}
            />
          )}
        </div>
      </div>
    </section>
  );
};

export default RegularChart;
